import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import styles from './index.module.less';
import PureVideo from '@base/lib/PureVideo';
import makeSingleClick from 'base/utils/makeSingleClick';
import Iconfont from '@base/lib/Iconfont';
import { isVideoUrl } from 'base/utils/file';
import { IThemeSwiperRef, ThemeSwiper } from '@base/lib/ThemeSwiper';
import { getCdnUrl } from 'base/utils/trans-path';
import Image from '@base/lib/ThemeImage';

export type IListItem = string | {
    src: string;
    backup?: string;
    poster?: string;
};

export type IOpenConfig = {
    list: Array<IListItem>;
    activeIndex?: number;
};

export type IPreviewerRenderProps = {
    open: (config: IOpenConfig) => void;
};

type ImagePreviewerProps = {
    videoAutoPlay?: boolean;
    children: (renderProps: IPreviewerRenderProps) => React.ReactNode;
};

type IModalRef = {
    open: (config: IOpenConfig) => void;
}

type IModalProps = {
    videoAutoPlay?: boolean;
};

const singleClick = makeSingleClick();

type IListItemProps = {
    item: IListItem;
    closeModal: any;
    toggleZoom: any;
}

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

function isVideoItem(item: IListItem): boolean {
    return typeof item !== 'string' || isVideoUrl(item);
}

const ListItem: React.FC<IListItemProps> = (props) => {
    const {
        item,
        closeModal,
        toggleZoom,
    } = props

    if (typeof item === 'string') {
        return isVideoUrl(item) ? (
            <PureVideo src={getCdnUrl(item)} controls control/>
        ) : (
            <div className="swiper-zoom-container">
                <Image
                    src={getCdnUrl(item)}
                    alt={''}
                    width="100%"
                    fit="contain"
                    background={false}
                    onClick={(e) => {
                        e.stopPropagation();
                        singleClick(
                            closeModal,
                            toggleZoom
                        )
                    }}
                />
            </div>
        );
    }


    return (
        <PureVideo
            src={[item.src, item.backup]}
            poster={item.poster}
            controls
            control
        />
    )
}

// eslint-disable-next-line react/display-name
export const PreviewerModal = forwardRef<IModalRef, IModalProps>((props, ref) => {
    const { videoAutoPlay = true } = props;
    const [visible, setVisible] = useState<boolean>(false);
    const [config, setConfig] = useState<IOpenConfig>({
        list: [],
        activeIndex: 0,
    });
    const [isVideoActive, setIsVideoActive] = useState<boolean>(false);
    const swiperRef = useRef<IThemeSwiperRef>(null);

    const getSwiper = () => swiperRef.current as IThemeSwiperRef;

    // 根据子组件判断点击背景是否可以
    const isSingle = config.list.length < 2;

    const stopAllVideos = () => {
        const videos = document.querySelectorAll('video');
        if (videos && videos.length > 0) {
            Array.prototype.forEach.call(videos, v => {
                v.pause();
            });
        }
    };

    const resetZoom = () => {
        getSwiper()?.zoomOut();
    };

    const toggleZoom = () => {
        getSwiper()?.zoomToggle();
    };

    const handleSlideChange = () => {
        resetZoom();
        stopAllVideos();
    };

    const handleSlideEnd = (swiper) => {
        const activeSlide = swiper.el.querySelector('.swiper-slide-active');
        if (activeSlide) {
            const videoEl = activeSlide.querySelector('video');
            if (videoEl) {
                setIsVideoActive(true)
                return;
            }
        }
        setIsVideoActive(false);
    };

    const closeModal = () => {
        stopAllVideos();
        resetZoom();
        setVisible(false);
    };

    useImperativeHandle(ref, () => ({
        open: config => {
            if (config?.list?.length) {
                setConfig(config);
                setVisible(true);
                setIsVideoActive(isVideoItem(config.list[config.activeIndex || 0]))
            }
        }
    }));

    useEffect(() => {
        if (!visible) {
            return;
        }
        // 视频自动播放
        if (videoAutoPlay && isVideoItem(config.list[config.activeIndex || 0])) {
            setTimeout(() => {
                const activeVideo = getSwiper()?.getSwiperEl()?.querySelector('.swiper-slide-active video') as HTMLVideoElement;
                if (activeVideo) {
                    activeVideo.currentTime = 0; // reset time
                    activeVideo.play();
                }
            }, 200);
        }


    }, [visible]);

    const swiperElem = (
        <ThemeSwiper
            ref={swiperRef}
            className="swiper"
            zoom
            loop
            navigation={!isPhone}
            pagination={isSingle ? false : { type: 'fraction', }}
            onSlideChangeTransitionStart={handleSlideChange}
            onSlideChangeTransitionEnd={handleSlideEnd}
            initialSlide={config.activeIndex}
            list={config.list}
            render={(item, index) => {
                return (
                    <ListItem
                        item={item}
                        closeModal={closeModal}
                        toggleZoom={toggleZoom}
                    />
                )
            }}
        />
    );

    return (
        <Modal
            open={visible}
            onCancel={closeModal}
            wrapClassName={styles.modal}
            footer={false}
            closable={isPhone}
            destroyOnClose
            wrapProps={{
                onClick: e => {
                    e.stopPropagation(); //阻止冒泡
                }
            }}
        >
            {isPhone ? swiperElem : (
                <div className={styles['pc-wrapper']}>
                    {swiperElem}
                    <div className={styles.options}>
                        {!isVideoActive && (
                            <>
                                <span className={styles.option} onClick={() => getSwiper().zoomIn()}>
                                    <Iconfont className="icon-add"/>
                                </span>
                                <span className={styles.option} onClick={() => getSwiper().zoomOut()}>
                                    <Iconfont className="icon-minus"/>
                                </span>
                            </>
                        )}

                        <span className={styles.option} onClick={closeModal}>
                            <Iconfont className="icon-x"/>
                        </span>
                    </div>
                </div>
            )}
        </Modal>
    );
});

// @ts-ignore
export const ImagePreviewer: React.FC<ImagePreviewerProps> = props => {
    const { children, videoAutoPlay } = props;
    const previewRef = useRef<IPreviewerRenderProps>(null);

    return typeof children !== 'function' ? children : (
        <>
            {children({
                open: (config: IOpenConfig) => singleClick(() => {
                    if (config.list.length > 0) {
                        previewRef?.current?.open?.(config);
                    }
                })
            })}

            <PreviewerModal ref={previewRef} videoAutoPlay={videoAutoPlay}/>
        </>
    );
};

export default ImagePreviewer;